import {
  Parser,
  parseTemplate,
  type ExerciseNode,
  type Expr,
  type TemplateNode,
  type Unit,
  type WorkoutNode,
} from '@gymcloud/mixedscript'

/** Reading the bits of a template the editor arranges itself around. */

export function scheduleOf(node: TemplateNode): string {
  const value = node.props.find((prop) => prop.name === 'schedule')?.value
  return value?.type === 'name' ? value.name : 'free'
}

/**
 * The template a script holds when its schedule is strict, and null for anything else — a
 * schedule with no cycle to land, or a script that does not parse, which will not run either.
 * What asks for an anchor asks for one exactly when this is not null.
 */
export function strictNode(script: string): TemplateNode | null {
  try {
    const node = parseTemplate(script)
    return scheduleOf(node) === 'strict' ? node : null
  } catch {
    return null
  }
}

/** Every exercise a workout holds, groups opened out, in the order they are written. */
export function exercisesOf(workout: WorkoutNode): readonly ExerciseNode[] {
  return workout.items.flatMap((item) =>
    item.type === 'exercise' ? [item.exercise] : item.exercises,
  )
}

export function cycleOf(node: TemplateNode): number | null {
  const value = node.props.find((prop) => prop.name === 'cycle')?.value
  return value?.type === 'number' ? value.value : null
}

/** Which day of the cycle a workout sits on, or null while it sits on none. */
export function dayOf(workout: WorkoutNode): number | null {
  const value = workout.props.find((prop) => prop.name === 'day')?.value
  return value?.type === 'number' ? value.value : null
}

/**
 * The first free day of the cycle after `after`, wrapping past the end of it to the days
 * before. Null when every day is taken.
 *
 * After rather than from the start of the cycle, because whatever is being placed belongs
 * beside what it came from: a Tuesday session duplicated is far likelier to be wanted on
 * Wednesday than on Monday.
 */
export function freeDayAfter(node: TemplateNode, after: number | null): number | null {
  const cycle = cycleOf(node)
  if (cycle === null || cycle <= 0) {
    return null
  }
  const taken = new Set(node.workouts.map((workout) => dayOf(workout)))
  const from = after ?? -1
  for (let step = 1; step <= cycle; step += 1) {
    const day = (((from + step) % cycle) + cycle) % cycle
    if (!taken.has(day)) {
      return day
    }
  }
  return null
}

/**
 * A strict template's workouts in the order their days fall.
 *
 * A strict schedule *is* its calendar, so the order the script happens to list its workouts in
 * is not an order anybody means — the days are. Sorting the script rather than the board keeps
 * one order across the editor: what is shown, what an index means, and what the script reads
 * as are all the same thing. Any schedule but strict is left exactly as written, since there
 * the order is the meaning.
 */
export function inDayOrder(node: TemplateNode): TemplateNode {
  if (scheduleOf(node) !== 'strict') {
    return node
  }
  // A workout with no day yet sorts to the end rather than to day zero, where it would shove
  // everything along on its way to being given one.
  const at = (workout: WorkoutNode): number => dayOf(workout) ?? Number.MAX_SAFE_INTEGER
  return { ...node, workouts: [...node.workouts].sort((a, b) => at(a) - at(b)) }
}

/** A number as the language writes it, which is what a property has to hold. */
export function number(value: number, unit: Unit): Expr {
  return new Parser(`${String(value)}${unit.suffix}`).parseExpr()
}
